import { FC } from 'react'
import {
  Datagrid, FilterList, FilterListItem, FunctionField, List,
  TextField, useGetList, useTranslate,
} from 'react-admin'
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew'
import StorefrontIcon from '@mui/icons-material/Storefront'
import SourceIcon from '@mui/icons-material/Source'
import BusinessIcon from '@mui/icons-material/Business'
import { Chip } from '@mui/material'
import { EnabledChip, FilterAside, MarketplaceChip } from '../../components'
import { xxllncColor } from '../../layout/theme/color'
import { ApplicationOut, McpServerOut } from '../../types/apiRequests'
import { getPerPage } from '../../utils/usePerPage'

// 4 decimals — MCP per-call cost is on the order of €0.0001-0.001; fewer would hide order-of-magnitude differences.
const EUR_4 = new Intl.NumberFormat('nl-NL', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 4,
  maximumFractionDigits: 4,
})

const McpServerFilters: FC = () => {
  const translate = useTranslate()
  const { data: applications } = useGetList<ApplicationOut>(
    'application',
    { pagination: { page: 1, perPage: 100 }, sort: { field: 'display_name', order: 'ASC' } },
  )

  return (
    <FilterAside>
      <FilterList label={translate('filters.status')} icon={<PowerSettingsNewIcon />}>
        <FilterListItem label="filters.active" value={{ enabled: true }} />
        <FilterListItem label="filters.inactive" value={{ enabled: false }} />
      </FilterList>

      <FilterList label={translate('filters.marketplace')} icon={<StorefrontIcon />}>
        <FilterListItem label="filters.marketplaceVisible" value={{ marketplace: true }} />
        <FilterListItem label="filters.marketplaceHidden" value={{ marketplace: false }} />
      </FilterList>

      <FilterList label={translate('filters.toolSource')} icon={<SourceIcon />}>
        {/* eslint-disable camelcase */}
        <FilterListItem label="data.toolSourceOpenapi" value={{ tool_source: 'openapi' }} />
        <FilterListItem label="data.toolSourceOdata" value={{ tool_source: 'odata' }} />
        <FilterListItem label="data.toolSourceManual" value={{ tool_source: 'manual' }} />
        {/* eslint-enable camelcase */}
      </FilterList>

      <FilterList label={translate('filters.application')} icon={<BusinessIcon />}>
        {applications?.map((app) => (
          <FilterListItem
            key={app.id}
            label={app.display_name}
            // eslint-disable-next-line camelcase
            value={{ application_id: app.id }}
          />
        ))}
      </FilterList>
    </FilterAside>
  )
}

const McpServerList: FC = () => {
  const translate = useTranslate()
  return (
    <List
      exporter={false}
      sort={{ field: 'lastModified', order: 'DESC' }}
      perPage={getPerPage()}
      aside={<McpServerFilters />}
    >
      <Datagrid bulkActionButtons={false} rowClick="show">
        <TextField source="display_name" label="data.displayName" />
        <FunctionField label="data.enabled" render={() => <EnabledChip />} />
        <FunctionField label="data.marketplaceVisible" render={() => <MarketplaceChip />} />
        <FunctionField<McpServerOut>
          label="data.application"
          render={(record) =>
            record.application_display_name ? (
              <Chip
                label={record.application_display_name}
                size="small"
                sx={{
                  fontWeight: 600,
                  fontSize: 11,
                  height: 22,
                  border: 'none',
                  bgcolor: xxllncColor.opDark10,
                  color: xxllncColor.dark100,
                }}
              />
            ) : (
              '—'
            )
          }
        />
        <FunctionField<McpServerOut>
          label="mcp.list.cost"
          render={(record) =>
            record.cost_per_1000_calls != null
              ? `${EUR_4.format(record.cost_per_1000_calls)} ${translate('mcp.list.costPer1k')}`
              : translate('workflow.list.costNotSet')
          }
        />
        <FunctionField<McpServerOut>
          label="mcp.list.links"
          render={(record) => String(record.link_count ?? 0)}
        />
        <FunctionField<McpServerOut>
          label="mcp.list.requests24h"
          render={(record) => String(record.requests_24h ?? 0)}
        />
      </Datagrid>
    </List>
  )
}

export default McpServerList
